(() => {
  const SELECTOR = '[data-cardveo-autoplay="true"]';
  const instances = new WeakMap();

  const initComponent = (component) => {
    if (!component || instances.has(component)) return;

    const slider = component.querySelector('.multicolumn-list.slider');
    const slides = slider ? Array.from(slider.querySelectorAll('.slider__slide')) : [];
    if (!slider || slides.length < 2) return;

    const progressBar = component.querySelector('.cardveo-autoplay-progress__bar');
    const mobileQuery = window.matchMedia('(max-width: 749px)');
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const interval = Math.max(3000, Number(component.dataset.autoplaySpeed || 5) * 1000);
    const movementStyle = component.dataset.autoplayStyle || 'focus';
    const duration = movementStyle === 'pulse' ? 720 : movementStyle === 'glide' ? 1180 : 980;

    let currentIndex = 0;
    let direction = 1;
    let autoplayTimer = null;
    let resumeTimer = null;
    let scrollTimer = null;
    let animationFrame = null;
    let isVisible = true;
    let isInteracting = false;
    let isProgrammatic = false;
    let intersectionObserver = null;

    const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

    const ease = (progress) => {
      if (movementStyle === 'pulse') return 1 - Math.pow(1 - progress, 3);

      if (movementStyle === 'glide') {
        return progress < 0.5
          ? 4 * progress * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      }

      return 1 - Math.pow(1 - progress, 4);
    };

    const nearestIndex = () => {
      const sliderRect = slider.getBoundingClientRect();
      let nearest = 0;
      let smallestDistance = Number.POSITIVE_INFINITY;

      slides.forEach((slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - sliderRect.left);
        if (distance < smallestDistance) {
          smallestDistance = distance;
          nearest = index;
        }
      });

      return nearest;
    };

    const setActiveSlide = (index) => {
      currentIndex = clamp(index, 0, slides.length - 1);
      slides.forEach((slide, slideIndex) => {
        slide.classList.toggle('is-cardveo-active', slideIndex === currentIndex);
      });
    };

    const stopProgress = () => {
      if (!progressBar) return;
      progressBar.style.transition = 'none';
      progressBar.style.transform = 'scaleX(0)';
    };

    const startProgress = (delay) => {
      if (!progressBar) return;
      stopProgress();
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          progressBar.style.transition = `transform ${delay}ms linear`;
          progressBar.style.transform = 'scaleX(1)';
        });
      });
    };

    const canAutoplay = () =>
      mobileQuery.matches &&
      !reducedMotionQuery.matches &&
      isVisible &&
      !document.hidden &&
      !isInteracting;

    const clearAutoplay = () => {
      window.clearTimeout(autoplayTimer);
      autoplayTimer = null;
      stopProgress();
    };

    const cancelAnimation = () => {
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
        animationFrame = null;
      }
      isProgrammatic = false;
      slider.style.scrollSnapType = '';
    };

    const scheduleNext = (delay = interval) => {
      clearAutoplay();
      if (!canAutoplay()) return;

      startProgress(delay);
      autoplayTimer = window.setTimeout(() => {
        const detectedIndex = nearestIndex();
        setActiveSlide(detectedIndex);

        let nextIndex = detectedIndex + direction;
        if (nextIndex >= slides.length) {
          direction = -1;
          nextIndex = Math.max(0, slides.length - 2);
        } else if (nextIndex < 0) {
          direction = 1;
          nextIndex = Math.min(slides.length - 1, 1);
        }

        const slide = slides[nextIndex];
        if (!slide) return;

        cancelAnimation();
        isProgrammatic = true;
        setActiveSlide(nextIndex);

        const sliderRect = slider.getBoundingClientRect();
        const slideRect = slide.getBoundingClientRect();
        const startScroll = slider.scrollLeft;
        const maxScroll = Math.max(0, slider.scrollWidth - slider.clientWidth);
        const targetScroll = clamp(startScroll + slideRect.left - sliderRect.left, 0, maxScroll);
        const startedAt = performance.now();
        slider.style.scrollSnapType = 'none';

        const animate = (now) => {
          if (!isProgrammatic) return;

          const progress = clamp((now - startedAt) / duration, 0, 1);
          slider.scrollLeft = startScroll + (targetScroll - startScroll) * ease(progress);

          if (progress < 1) {
            animationFrame = window.requestAnimationFrame(animate);
            return;
          }

          slider.scrollLeft = targetScroll;
          slider.style.scrollSnapType = '';
          animationFrame = null;
          isProgrammatic = false;
          scheduleNext();
        };

        animationFrame = window.requestAnimationFrame(animate);
      }, delay);
    };

    const pauseForInteraction = () => {
      isInteracting = true;
      window.clearTimeout(resumeTimer);
      clearAutoplay();
      cancelAnimation();
      setActiveSlide(nearestIndex());
    };

    const resumeAfterInteraction = () => {
      isInteracting = false;
      window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(() => {
        setActiveSlide(nearestIndex());
        scheduleNext();
      }, 900);
    };

    const onScroll = () => {
      if (isProgrammatic) return;
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(() => setActiveSlide(nearestIndex()), 100);
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        clearAutoplay();
        cancelAnimation();
      } else {
        setActiveSlide(nearestIndex());
        scheduleNext();
      }
    };

    const onMediaChange = () => {
      clearAutoplay();
      cancelAnimation();
      setActiveSlide(nearestIndex());

      if (canAutoplay()) {
        scheduleNext();
      } else {
        slides.forEach((slide) => slide.classList.remove('is-cardveo-active'));
      }
    };

    const onPointerDown = () => pauseForInteraction();
    const onPointerUp = () => resumeAfterInteraction();
    const onFocusIn = () => pauseForInteraction();
    const onFocusOut = () => resumeAfterInteraction();

    component.addEventListener('pointerdown', onPointerDown, { passive: true });
    component.addEventListener('pointerup', onPointerUp, { passive: true });
    component.addEventListener('pointercancel', onPointerUp, { passive: true });
    component.addEventListener('focusin', onFocusIn);
    component.addEventListener('focusout', onFocusOut);
    slider.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('visibilitychange', onVisibilityChange);

    if (mobileQuery.addEventListener) {
      mobileQuery.addEventListener('change', onMediaChange);
      reducedMotionQuery.addEventListener('change', onMediaChange);
    } else {
      mobileQuery.addListener(onMediaChange);
      reducedMotionQuery.addListener(onMediaChange);
    }

    if ('IntersectionObserver' in window) {
      intersectionObserver = new IntersectionObserver(
        (entries) => {
          isVisible = Boolean(entries[0]?.isIntersecting);
          if (isVisible) {
            setActiveSlide(nearestIndex());
            scheduleNext();
          } else {
            clearAutoplay();
            cancelAnimation();
          }
        },
        { threshold: 0.45 }
      );
      intersectionObserver.observe(component);
    }

    setActiveSlide(nearestIndex());
    scheduleNext();

    const cleanup = () => {
      window.clearTimeout(autoplayTimer);
      window.clearTimeout(resumeTimer);
      window.clearTimeout(scrollTimer);
      cancelAnimation();
      stopProgress();
      intersectionObserver?.disconnect();

      component.removeEventListener('pointerdown', onPointerDown);
      component.removeEventListener('pointerup', onPointerUp);
      component.removeEventListener('pointercancel', onPointerUp);
      component.removeEventListener('focusin', onFocusIn);
      component.removeEventListener('focusout', onFocusOut);
      slider.removeEventListener('scroll', onScroll);
      document.removeEventListener('visibilitychange', onVisibilityChange);

      if (mobileQuery.removeEventListener) {
        mobileQuery.removeEventListener('change', onMediaChange);
        reducedMotionQuery.removeEventListener('change', onMediaChange);
      } else {
        mobileQuery.removeListener(onMediaChange);
        reducedMotionQuery.removeListener(onMediaChange);
      }

      instances.delete(component);
    };

    instances.set(component, cleanup);
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach(initComponent);
    if (scope.matches?.(SELECTOR)) initComponent(scope);
  };

  const cleanupScope = (scope) => {
    scope.querySelectorAll?.(SELECTOR).forEach((component) => instances.get(component)?.());
    if (scope.matches?.(SELECTOR)) instances.get(scope)?.();
  };

  const boot = () => initAll(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => cleanupScope(event.target));
})();
